// Household-local time handling. Uses Intl.DateTimeFormat#formatToParts (Safari 11+) only;
// month/weekday names are our own so output is identical on every engine.

export type DateKey = string; // YYYY-MM-DD in the household's calendar
export type HHMM = string; // 24h "HH:MM"

export const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'] as const;
export const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
] as const;

export interface ZonedParts {
  dateKey: DateKey;
  hhmm: HHMM;
  hour: number;
  minute: number;
  second: number;
}

const formatterCache: Record<string, Intl.DateTimeFormat> = {};

function formatter(tz: string): Intl.DateTimeFormat {
  let f = formatterCache[tz];
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
    formatterCache[tz] = f;
  }
  return f;
}

const pad2 = (n: number) => (n < 10 ? '0' : '') + n;

export function zonedParts(date: Date, tz: string): ZonedParts {
  const out: Record<string, string> = {};
  for (const p of formatter(tz).formatToParts(date)) out[p.type] = p.value;
  // Some engines render midnight as "24" with hour12:false.
  const hour = Number(out.hour) % 24;
  const minute = Number(out.minute);
  return {
    dateKey: `${out.year}-${out.month}-${out.day}`,
    hhmm: `${pad2(hour)}:${pad2(minute)}`,
    hour,
    minute,
    second: Number(out.second),
  };
}

function keyToUTC(key: DateKey): Date {
  const [y, m, d] = key.split('-').map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d));
}

export function addDays(key: DateKey, n: number): DateKey {
  const d = keyToUTC(key);
  d.setUTCDate(d.getUTCDate() + n);
  return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`;
}

export function daysBetween(from: DateKey, to: DateKey): number {
  return Math.round((keyToUTC(to).getTime() - keyToUTC(from).getTime()) / 86400000);
}

export function weekdayOf(key: DateKey): number {
  return keyToUTC(key).getUTCDay();
}

/** "Sunday, October 18" */
export function eyebrowDate(key: DateKey): string {
  const d = keyToUTC(key);
  return `${WEEKDAYS[d.getUTCDay()]}, ${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}`;
}

/** 1 → "1st", 2 → "2nd", 11 → "11th", 23 → "23rd" */
export function ordinal(n: number): string {
  const teen = n % 100 >= 11 && n % 100 <= 13;
  const suffix = teen ? 'th' : ({ 1: 'st', 2: 'nd', 3: 'rd' } as Record<number, string>)[n % 10] || 'th';
  return `${n}${suffix}`;
}

/** "Sunday 18th October" (the calendar widget's heading) */
export function ordinalDate(key: DateKey): string {
  const d = keyToUTC(key);
  return `${WEEKDAYS[d.getUTCDay()]} ${ordinal(d.getUTCDate())} ${MONTHS[d.getUTCMonth()]}`;
}

/** "Sunday, October 18, 2026" */
export function longDate(key: DateKey): string {
  return `${eyebrowDate(key)}, ${keyToUTC(key).getUTCFullYear()}`;
}

/** "Today, Oct 18" | "Tomorrow" | "Yesterday" | "Mon, Oct 19" */
export function dayLabel(key: DateKey, todayKey: DateKey): string {
  const d = keyToUTC(key);
  const short = `${MONTHS[d.getUTCMonth()]!.slice(0, 3)} ${d.getUTCDate()}`;
  const diff = daysBetween(todayKey, key);
  if (diff === 0) return `Today, ${short}`;
  if (diff === 1) return 'Tomorrow';
  if (diff === -1) return 'Yesterday';
  return `${WEEKDAYS[d.getUTCDay()]!.slice(0, 3)}, ${short}`;
}

/** Short weekday for forecast rows: "Today" or "Mon". */
export function shortWeekday(key: DateKey, todayKey: DateKey): string {
  return key === todayKey ? 'Today' : WEEKDAYS[weekdayOf(key)]!.slice(0, 3);
}

/** "07:00" (24h) or "7:00 AM" (12h). */
export function formatTime(hhmm: HHMM, h24: boolean): string {
  if (h24) return hhmm;
  const [h, m] = hhmm.split(':').map(Number) as [number, number];
  return `${h % 12 || 12}:${pad2(m)} ${h >= 12 ? 'PM' : 'AM'}`;
}

/** Big clock split: { main: "8:30", suffix: "AM" } or { main: "08:30", suffix: "" }. */
export function clockFace(hhmm: HHMM, h24: boolean): { main: string; suffix: string } {
  if (h24) return { main: hhmm, suffix: '' };
  const [h, m] = hhmm.split(':').map(Number) as [number, number];
  return { main: `${h % 12 || 12}:${pad2(m)}`, suffix: h >= 12 ? 'PM' : 'AM' };
}

export type DayPart = 'morning' | 'afternoon' | 'evening';

export function dayPart(hour: number): DayPart {
  if (hour >= 5 && hour < 12) return 'morning';
  if (hour >= 12 && hour < 18) return 'afternoon';
  return 'evening';
}

export function greeting(hour: number): string {
  return { morning: 'Good morning.', afternoon: 'Good afternoon.', evening: 'Good evening.' }[dayPart(hour)];
}

/** Milliseconds until the next wall-clock minute boundary. */
export function msUntilNextMinute(now: Date): number {
  return 60000 - (now.getTime() % 60000) + 20;
}

export function isHHMM(v: string): boolean {
  return /^([01]\d|2[0-3]):[0-5]\d$/.test(v);
}
